import type { SurveyOverallScores, SurveyScoresByCategory } from '@engagement-web-app/common';
import {
    BarController,
    BarElement,
    CategoryScale,
    type ChartData,
    Chart as ChartJS,
    type ChartOptions,
    Legend,
    LinearScale,
    type Plugin,
    Title,
    Tooltip,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { transpose } from 'ramda';
import { useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { ErrorOverlay } from '@/components/ErrorOverlay';
import { Flex } from '@/components/Flex';
import { Section, SectionHeader } from '@/components/Section';
import { Select } from '@/components/Select';
import { Skeleton } from '@/components/Skeleton';
import { formatDateToLabel, formatNumber, type MetricOption, metricOptions, padArray } from '@/utils';
import { customCanvasBackgroundColorOptions, customCanvasBackgroundColorPlugin } from '@/utils/chart';
import * as sharedStyles from '../styles.css';

ChartJS.register(CategoryScale, LinearScale, BarController, BarElement, Title, Tooltip, Legend);

type ScoreGrade = 'S' | 'A' | 'B' | 'C' | 'D';

/**
 * Maps a deviation score to its grade, judged on the value rounded to one
 * decimal place (as the bar labels show it) so a bar's color always matches
 * its label: 60.0+ is S, 55.2+ is A, 44.8+ is B, 37.0+ is C, below 37.0 is D.
 */
function getGrade(score: number): ScoreGrade {
    const rounded = Math.round(score * 10) / 10;
    if (rounded >= 60) return 'S';
    if (rounded >= 55.2) return 'A';
    if (rounded >= 44.8) return 'B';
    if (rounded >= 37) return 'C';
    return 'D';
}

const gradeColorMap = new Map<ScoreGrade, string>([
    ['S', 'rgb(128, 0, 128)'],
    ['A', 'rgb(0, 112, 212)'],
    ['B', 'rgb(0, 230, 84)'],
    ['C', 'rgb(230, 180, 0)'],
    ['D', 'rgb(182, 0, 5)'],
]);

const previousSurveyColor = 'rgb(153, 153, 153)';

const averageLineColor = 'rgb(75, 85, 99)';

const averageLineLabels: Record<MetricOption, string> = {
    di: '全社平均（DI）',
    deviation: '全社平均（偏差値）',
};

/**
 * Draws a dashed horizontal line at the `averageLine` plugin option's `value`
 * (on the `y` scale), behind the bars — hooking `beforeDatasetsDraw` runs
 * this after the chart background but before any dataset is painted.
 */
const averageLinePlugin: Plugin<'bar'> = {
    id: 'averageLine',
    beforeDatasetsDraw: (chart, _, pluginOptions) => {
        const { value } = pluginOptions as { value: number | null };
        if (value === null || value === undefined) return;

        const {
            ctx,
            chartArea: { left, right },
            scales: { y },
        } = chart;

        const yPixel = y.getPixelForValue(value);

        ctx.save();
        ctx.strokeStyle = averageLineColor;
        ctx.lineWidth = 1.5;
        ctx.setLineDash([6, 4]);
        ctx.beginPath();
        ctx.moveTo(left, yPixel);
        ctx.lineTo(right, yPixel);
        ctx.stroke();
        ctx.restore();
    },
};

function averageLineOptions(value: number | null) {
    return { averageLine: { value } } as const;
}

/** Company-wide scores of the selected survey, drawn as the dashed average line. */
type CompanyAverageScores = Pick<SurveyOverallScores, 'di' | 'deviation'>;

type SurveyCategoriesComparisonChartProps = {
    /** `null` while still loading. */
    scores: SurveyScoresByCategory[] | null;
    /** `null` while still loading. */
    companyAverage: CompanyAverageScores | null;
    /** Opt-in: omit when this data has no independent failure mode to report. */
    isError?: boolean;
    onRetry?: () => void;
};

export function SurveyCategoriesComparisonChart({
    scores,
    companyAverage,
    isError,
    onRetry,
}: SurveyCategoriesComparisonChartProps) {
    if (isError) {
        return <ErrorOverlay active onRetry={onRetry} message="年度別カテゴリー比較の取得に失敗しました。" />;
    }

    if (!scores || !companyAverage) return <SurveyCategoriesComparisonChartSkeleton />;

    return <SurveyCategoriesComparisonChartContent scores={scores} companyAverage={companyAverage} />;
}

type SurveyCategoriesComparisonChartContentProps = {
    [K in keyof Pick<SurveyCategoriesComparisonChartProps, 'scores' | 'companyAverage'>]: NonNullable<
        SurveyCategoriesComparisonChartProps[K]
    >;
};

type CategoryScores = SurveyScoresByCategory['categories'][number];

// Split from the buffer above so its hooks are never called conditionally.
function SurveyCategoriesComparisonChartContent({
    scores,
    companyAverage,
}: SurveyCategoriesComparisonChartContentProps) {
    const [span, setSpan] = useState<number>(2);
    const [selectedMetric, setSelectedMetric] = useState<MetricOption>('di');

    const maxSpan = scores.length - 1;

    const categoryMap = useMemo(() => {
        const map = new Map<string, CategoryScores>();
        for (const entry of scores) {
            for (const category of entry.categories) {
                map.set(`${entry.surveyId}::${category.id}`, category);
            }
        }
        return map;
    }, [scores]);

    const averageValue = companyAverage[selectedMetric];
    const averageLabel = averageLineLabels[selectedMetric];

    const data: ChartData<'bar'> = useMemo(() => {
        const surveysToDisplay = scores.slice(0, span);

        if (surveysToDisplay.length === 0) {
            return {
                labels: [],
                datasets: [],
            };
        }

        const [selectedSurvey, ...pastSurveys] = surveysToDisplay;

        /**
         * Resolve the mapping of categories across surveys.
         *
         * Related categories are grouped together in the same array, with the first element being the category
         * from the selected survey and subsequent elements being the corresponding categories from past surveys.
         *
         * `transpose` is used to convert the array of arrays into a format suitable for Chart.js datasets,
         * where each dataset corresponds to a survey and contains the scores for the related categories.
         *
         * Each category in the selected survey is mapped to its previous version in past surveys, if they exist.
         * If a category does not have a previous version, it is padded with nulls to maintain alignment across the datasets.
         *
         * @example
         * [
         *  [1, 1, 1], // Selected survey category and its previous versions
         *  [2, 2, null], // Another selected survey category and its previous versions
         *  [3, null, null], // Another selected survey category with no previous versions
         * ]
         *
         * // After transpose:
         * [
         *  [1, 2, 3], // Categories for the selected survey
         *  [1, 2, null], // Categories for the first past survey
         *  [1, null, null], // Categories for the second past survey
         * ]
         */
        const mappedCategories = transpose(
            selectedSurvey.categories.reduce<(CategoryScores | null)[][]>((acc, category) => {
                const mapped: (CategoryScores | null)[] = [category];

                if (category.previousCategoryId === null) {
                    acc.push(padArray(mapped, surveysToDisplay.length, null));
                    return acc;
                }

                for (let i = 0; i < pastSurveys.length; i++) {
                    const pastSurvey = pastSurveys[i];
                    const categoryMapKey = `${pastSurvey.surveyId}::${category.previousCategoryId}`;
                    const previousCategory = categoryMap.get(categoryMapKey);
                    if (!previousCategory) break;
                    mapped.push(previousCategory);
                    if (previousCategory.previousCategoryId === null) break;
                }

                acc.push(padArray(mapped, surveysToDisplay.length, null));
                return acc;
            }, [])
        );

        return {
            labels: selectedSurvey.categories.map((c) => c.name),
            datasets: mappedCategories
                .map((categories, i) => {
                    const survey = surveysToDisplay[i]!;
                    const isSelectedSurvey = i === 0;

                    return {
                        label: formatDateToLabel(survey.surveyDate),
                        data: categories.map((c) => (c ? (c[selectedMetric] ?? null) : null)),
                        backgroundColor: isSelectedSurvey
                            ? categories.map((c) => {
                                  const score = c?.[selectedMetric];
                                  if (score === null || score === undefined) return 'gray';
                                  // Only deviation uses grades, DI uses a unique color.
                                  return selectedMetric === 'deviation'
                                      ? gradeColorMap.get(getGrade(score))!
                                      : '#3b82f6';
                              })
                            : previousSurveyColor,
                    };
                })
                .reverse(),
        };
    }, [scores, span, categoryMap, selectedMetric]);

    const options: ChartOptions<'bar'> = useMemo(() => {
        return {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
                y: {
                    grace: '5',
                    grid: { color: '#e5e7eb' },
                },
                x: {
                    grid: { color: '#e5e7eb' },
                },
            },
            plugins: {
                legend: {
                    display: false,
                },
                datalabels: {
                    anchor: 'end',
                    align: 'end',
                    formatter: (value: number | null) => (value === null ? '' : formatNumber(value)),
                },
                tooltip: {
                    mode: 'index',
                    callbacks: {
                        title: (items) => items[0].label,
                        label: (item) =>
                            `${item.dataset.label}：${item.parsed.y === null || item.parsed.y === undefined ? 'N/A' : formatNumber(item.parsed.y)}`,
                        afterBody: () =>
                            `${averageLabel}：${averageValue === null ? 'N/A' : formatNumber(averageValue)}`,
                    },
                },
                ...customCanvasBackgroundColorOptions,
                ...averageLineOptions(averageValue),
            },
        };
    }, [averageValue, averageLabel]);

    return (
        <Section>
            <SectionHeader
                title="年度別カテゴリー比較"
                description="カテゴリーごとのスコアを年度別に並べて比較します。指標は DI と偏差値を切り替えられ、表示年数も選択できます。"
            />

            <Flex direction="column" gap="6" align="start">
                <Flex gap="4">
                    <Select value={selectedMetric} onChange={(e) => setSelectedMetric(e.target.value as MetricOption)}>
                        {metricOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Select>

                    {maxSpan > 0 && (
                        <Select value={span} onChange={(e) => setSpan(Number(e.target.value))}>
                            {Array.from({ length: maxSpan }, (_, i) => 2 + i).map((s) => (
                                <option key={s} value={s}>
                                    {s}年
                                </option>
                            ))}
                        </Select>
                    )}
                </Flex>

                <div className={sharedStyles.scoreDistributionLegendContainer}>
                    {selectedMetric === 'deviation' && (
                        <>
                            <div>
                                {Array.from(gradeColorMap.keys()).map((grade) => {
                                    return (
                                        <div
                                            key={grade}
                                            className={sharedStyles.scoreDistributionLegend}
                                            style={{ backgroundColor: gradeColorMap.get(grade) }}
                                        >
                                            {grade}
                                        </div>
                                    );
                                })}
                            </div>
                            今回
                            <div
                                className={sharedStyles.scoreDistributionLegend}
                                style={{ backgroundColor: previousSurveyColor }}
                            />
                            前回
                        </>
                    )}
                    <div className={sharedStyles.averageLineLegend} style={{ borderTopColor: averageLineColor }} />
                    {averageLabel}
                </div>

                <div className={sharedStyles.chartContainer}>
                    <Bar
                        data={data}
                        options={options}
                        plugins={[ChartDataLabels, customCanvasBackgroundColorPlugin, averageLinePlugin]}
                    />
                </div>
            </Flex>
        </Section>
    );
}

export function SurveyCategoriesComparisonChartSkeleton() {
    return (
        <Section>
            <Flex direction="column" gap="6" align="start">
                <Flex direction="column" gap="2" fullWidth>
                    <Skeleton.Line width="40%" height="xl" />
                    <Skeleton.Line width="70%" height="sm" />
                </Flex>

                <Flex gap="4">
                    <Skeleton.Box width={120} height={42} />
                    <Skeleton.Box width={120} height={42} />
                </Flex>

                <Skeleton.Box width="100%" height={300} />
            </Flex>
        </Section>
    );
}
